import {Injectable} from '@angular/core';
import {Subscription} from 'rxjs';
import {skip} from 'rxjs/operators';
import {ListenerGroup} from './shared/keyboard-viewport.util';
import {MmKeyboardStateService} from './keyboard-state.service';

const PANEL_SELECTOR = '.ng-dropdown-panel';
const APPENDED_PANEL_SELECTOR = 'body > .ng-dropdown-panel';
const OPEN_SELECT_SELECTOR = 'ng-select.ng-select-opened';
const GUARD_WINDOW_MS = 2500;

// The filter-select wrapper closes its ng-select on any scroll event it did not originate. Opening the
// keyboard produces several gestureless scrolls (WebKit's clamp, the scroll handoff, WebKit's animated
// caret reveal ~1.3s later), so a panel opened at the bottom of a form closes itself the moment the
// keyboard expands. ng-select's own core never closes on scroll; only the wrapper does.
//
// The panel is appended to body and positioned once at open time, against the layout viewport: it does
// not follow its control, and neither ng-select's drop-direction nor the wrapper's iPad variant knows
// the keyboard covers the lower band. Both are corrected here, from measurements only, by translating
// the panel — the one property ng-select and the wrapper never write on it (ng-select's own translate
// sits on the inner content panel).
@Injectable({providedIn: 'root'})
export class MmKeyboardDropdownGuardService {

  private listeners = new ListenerGroup();
  private stateSub: Subscription | null = null;
  private observer: MutationObserver | null = null;
  private touchActive = false;
  private windowEndsAt = 0;
  private frame: number | null = null;
  private panel: HTMLElement | null = null;
  private translate = 0;

  constructor(private keyboardState: MmKeyboardStateService) {}

  start(): void {
    if (this.listeners.active || typeof document === 'undefined') {
      return;
    }
    // registered at boot so it precedes the listener the wrapper adds per panel open: same target, same
    // phase, so capture order is registration order and this handler can suppress that one
    this.listeners.add(window, 'scroll', event => this.onScroll(event), {capture: true});
    this.listeners.add(document, 'touchstart', () => {
      this.touchActive = true;
    }, {capture: true, passive: true});
    this.listeners.add(document, 'touchend', event => this.onTouchEnd(event as TouchEvent), {capture: true, passive: true});
    this.listeners.add(document, 'touchcancel', event => this.onTouchEnd(event as TouchEvent), {capture: true, passive: true});
    this.stateSub = this.keyboardState.keyboardOpen$.pipe(skip(1)).subscribe(() => this.openGuardWindow());
    this.observer = new MutationObserver(() => this.syncPanel());
    this.observer.observe(document.body, {childList: true});
  }

  stop(): void {
    this.listeners.clear();
    this.stateSub?.unsubscribe();
    this.stateSub = null;
    this.observer?.disconnect();
    this.observer = null;
    this.releasePanel();
  }

  private onTouchEnd(event: TouchEvent): void {
    this.touchActive = event.touches.length > 0;
  }

  private openGuardWindow(): void {
    this.windowEndsAt = performance.now() + GUARD_WINDOW_MS;
  }

  private onScroll(event: Event): void {
    if (this.touchActive || !this.keyboardState.isKeyboardOpen || performance.now() > this.windowEndsAt) {
      return;
    }
    const panel = this.panel || document.querySelector(PANEL_SELECTOR);
    const target = event.target;
    if (!panel || (target instanceof Node && panel.contains(target))) {
      return;
    }
    event.stopImmediatePropagation();
  }

  private syncPanel(): void {
    const panel = document.querySelector<HTMLElement>(APPENDED_PANEL_SELECTOR);
    if (panel === this.panel) {
      return;
    }
    this.releasePanel();
    this.panel = panel;
    if (panel) {
      this.frame = requestAnimationFrame(() => this.place());
    }
  }

  private releasePanel(): void {
    if (this.frame !== null) {
      cancelAnimationFrame(this.frame);
      this.frame = null;
    }
    this.panel = null;
    this.translate = 0;
  }

  // absolute targeting, not follow-the-baseline: a dropup panel is bottom-anchored to body, so a
  // resize-model keyboard (Android WebView shrinks the body) moves the UNTRANSLATED panel by itself —
  // an offset kept relative to the anchor then walks the panel off-screen. Per frame the untranslated
  // placement is recovered (rect minus own translate), the desired spot is derived from the anchor and
  // the visual viewport only, and the translate is their difference. Without a keyboard the desired
  // spot is where ng-select already put the panel, so the translate stays ~0 by geometry.
  private place(): void {
    this.frame = null;
    const panel = this.panel;
    const anchor = document.querySelector<HTMLElement>(OPEN_SELECT_SELECTOR);
    if (!panel || !panel.isConnected || !anchor) {
      return;
    }
    const anchorRect = anchor.getBoundingClientRect();
    const panelRect = panel.getBoundingClientRect();
    const height = panelRect.height;
    const placedTop = panelRect.top - this.translate;
    const viewport = window.visualViewport;
    const viewportTop = viewport ? viewport.offsetTop : 0;
    const viewportBottom = viewport ? viewport.offsetTop + viewport.height : document.documentElement.clientHeight;
    let desiredTop = anchorRect.bottom;
    if (desiredTop + height > viewportBottom) {
      desiredTop = anchorRect.top - height;
    }
    desiredTop = Math.max(desiredTop, viewportTop);
    const next = Math.round(desiredTop - placedTop);
    // dead-band: ng-select's own placement differs from the derived spot by a rounding pixel or two —
    // rewriting the transform for that would jitter every frame
    if (Math.abs(next - this.translate) > 2) {
      this.translate = next;
      panel.style.transform = `translateY(${next}px)`;
    }
    this.frame = requestAnimationFrame(() => this.place());
  }
}
